import React, { useEffect, useState } from "react";
import { Button, Input, message, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getExcelData, getHarvestedPlots } from "../../../utils/api";
import { UI_DATE_FORMAT } from "../../../consts";
import { LoadingIcon } from "../../../components";

const SpinachTable = ({
  currentSite,
  currentCrop,
  currentGreenhouse,
  currentOrg,
  currentPlot,
  tableData,
  setTableData,
  plotData,
  setAllRowTableData,
}) => {
  const [rawTableData, setRawTableData] = useState([]);
  const [forecastData, setForecastData] = useState(null);
  const [showYieldColumn, setShowYieldColumn] = useState(false);
  const [loading, setLoading] = useState(true);

  const [greenHouseLabel, setGreenHouseLabel] = useState("Bay");
  const [blockLabel, setBlockLabel] = useState("Row");

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (currentGreenhouse) {
      fetchExcelData();
    }
  }, [currentGreenhouse]);

  useEffect(() => {
    if (currentSite) {
      const sm = currentSite.metadata[currentCrop?.name] || {};
      if (sm.greenhouse_name) {
        setGreenHouseLabel(sm.greenhouse_name);
      }
      if (sm.block_name) {
        setBlockLabel(sm.block_name);
      }
    }
  }, [currentSite, currentCrop]);

  useEffect(() => {
    if (
      currentOrg &&
      currentSite &&
      currentCrop &&
      currentGreenhouse &&
      currentPlot
    ) {
      fetchForecastData();
    }
  }, [currentOrg, currentSite, currentCrop, currentGreenhouse, currentPlot]);

  useEffect(() => {
    if (rawTableData?.length && forecastData !== null && currentPlot) {
      extractTableDataFromRawData(rawTableData);
    }
  }, [rawTableData, forecastData, currentPlot]);

  const fetchExcelData = async () => {
    setLoading(true);
    try {
      const response = await getExcelData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        currentGreenhouse.name,
      );
      const rawData = response?.data?.data?.rows || [];
      setRawTableData(rawData);
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Oops! Error fetching table data!";
      messageApi.open({ type: "error", content: msg });
    } finally {
      setLoading(false);
    }
  };

  const fetchForecastData = async () => {
    try {
      if (!currentGreenhouse?.id || !plotData?.length) return;
      const greenhouseIds = [currentGreenhouse.id];
      const plotIds = plotData.map((p) => p.id);

      const resp = await getHarvestedPlots(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        greenhouseIds,
        plotIds,
      );
      const harvestedData = resp?.data?.data;
      const fcData = {};
      let yieldColumnVisible = false;

      harvestedData?.greenhouses?.forEach((gh) => {
        if (gh.id === currentGreenhouse.id) {
          gh.plots?.forEach((p) => {
            fcData[p.id] = {
              harvestDate: p.harvested_date,
              yield: p.total_yield,
            };
            if (p.id === currentPlot.id && p.total_yield !== undefined) {
              yieldColumnVisible = true;
            }
          });
        }
      });

      setForecastData(fcData);
      setShowYieldColumn(yieldColumnVisible);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Could not fetch forecast data!",
      });
    }
  };

  function filterByNameAndBlock(name, objects) {
    if (!objects) return [];
    const numberMatch = name.match(/\d+/);
    if (!numberMatch) return [];
    const number = parseInt(numberMatch[0], 10);

    const parseBlock = (block) => {
      if (!block) return null;
      const blockStr = block.toString().trim();
      if (blockStr.includes("-")) {
        const rangeParts = blockStr.replace(/\s+/g, "").split("-");
        if (rangeParts.length === 2) {
          const start = parseInt(rangeParts[0], 10);
          const end = parseInt(rangeParts[1], 10);
          if (!isNaN(start) && !isNaN(end)) {
            return [start, end];
          }
        }
        return null;
      } else {
        const num = parseInt(blockStr, 10);
        if (isNaN(num)) return null;
        return [num, num];
      }
    };

    return objects.filter((obj) => {
      const parsed = parseBlock(obj["Row"]);
      if (!parsed) return false;
      const [start, end] = parsed;
      return start <= number && number <= end;
    });
  }

  function extractTableDataFromRawData(rawData) {
    if (!plotData || plotData.length === 0) return;
    const allRowOutput = {};
    let finalTable = [];

    plotData.forEach((plt) => {
      const rowData = filterByNameAndBlock(plt.name, rawData);
      rowData.sort(
        (a, b) => new Date(a["Planting Date"]) - new Date(b["Planting Date"]),
      );
      const latestRow = rowData[rowData.length - 1];
      if (!latestRow) return;

      const tableDataObj = {
        bay: latestRow["Bay"],
        harvestDate: latestRow["Harvest Date"],
        plantingDate: latestRow["Planting Date"],
        block: latestRow["Row"],
        variety: latestRow["Variety"],
      };

      // If forecast data has harvest date
      if (forecastData?.[plt.id]?.harvestDate) {
        tableDataObj.harvestDate = forecastData[plt.id].harvestDate;
      }
      // If forecast data has yield
      if (
        forecastData?.[plt.id]?.yield !== null &&
        forecastData?.[plt.id]?.yield !== undefined
      ) {
        tableDataObj.yield = forecastData[plt.id].yield;
      }

      allRowOutput[plt.name] = tableDataObj;
      if (plt.name === currentPlot?.name) {
        finalTable.push(tableDataObj);
      }
    });

    setAllRowTableData(allRowOutput);
    setTableData(finalTable);
  }

  const baseColumns = [
    {
      title: "Planting Date",
      dataIndex: "plantingDate",
      key: "plantingDate",
      sorter: (a, b) => dayjs(a.plantingDate).diff(dayjs(b.plantingDate)),
      render: (text) => (text ? dayjs(text).format(UI_DATE_FORMAT) : "-"),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Date"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
        </div>
      ),
      onFilter: (value, record) =>
        dayjs(record.plantingDate).format(UI_DATE_FORMAT).includes(value),
    },
    {
      title: "Harvest Date",
      dataIndex: "harvestDate",
      key: "harvestDate",
      sorter: (a, b) => dayjs(a.harvestDate).diff(dayjs(b.harvestDate)),
      render: (text) => (text ? dayjs(text).format(UI_DATE_FORMAT) : "-"),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Date"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
        </div>
      ),
      onFilter: (value, record) =>
        dayjs(record.harvestDate).format(UI_DATE_FORMAT).includes(value),
    },
    {
      title: greenHouseLabel,
      dataIndex: "bay",
      key: "bay",
      sorter: (a, b) => (a.bay || "").localeCompare(b.bay || ""),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Bay"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
        </div>
      ),
      onFilter: (value, record) =>
        record.bay?.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: blockLabel,
      dataIndex: "block",
      key: "block",
      sorter: (a, b) => (a.block || "").localeCompare(b.block || ""),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Block"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
        </div>
      ),
      onFilter: (value, record) =>
        record.block?.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Variety",
      dataIndex: "variety",
      key: "variety",
      sorter: (a, b) => (a.variety || "").localeCompare(b.variety || ""),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Variety"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
        </div>
      ),
      onFilter: (value, record) =>
        record.variety?.toLowerCase().includes(value.toLowerCase()),
    },
  ];

  const columns = showYieldColumn
    ? [
        ...baseColumns,
        {
          title:
            currentSite?.metadata?.[currentCrop.name]
              ?.FORECAST_YIELD_KEY_NAME_FOR_TABLE || "Yield",
          dataIndex: "yield",
          key: "yield",
          sorter: (a, b) => (a.yield || 0) - (b.yield || 0),
          render: (val) => (val !== null && val !== undefined ? val : "-"),
        },
      ]
    : baseColumns;

  return (
    <>
      {contextHolder}
      {loading ? (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <LoadingIcon height="100px" />
        </div>
      ) : tableData && tableData.length > 0 ? (
        <Table
          dataSource={tableData}
          columns={columns}
          pagination={false}
          className="mb-5 w-full"
          scroll={{ x: 768 }}
          rowKey={(record, idx) => idx}
        />
      ) : (
        <div className="text-center text-primaryBlue mb-3">
          No table data available
        </div>
      )}
    </>
  );
};

export { SpinachTable };
